import { Children, cloneElement, createContext, isValidElement, useContext, useId, type ReactElement, type ReactNode } from 'react';
import { useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import { ariaProps } from '@repletoryreach/sdk-utils/a11y';
import './Tabs.css';

export type TabsOrientation = 'horizontal' | 'vertical';
export type TabsVariant = 'default' | 'outline' | 'pills';

export interface TabsProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  orientation?: TabsOrientation;
  variant?: TabsVariant;
  /** Color of the active indicator. */
  color?: string;
  children?: ReactNode;
  className?: string;
}

interface TabsContextValue {
  value: string;
  setValue: (v: string) => void;
  orientation: TabsOrientation;
  variant: TabsVariant;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);
function useTabs(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs.List / Tab / Panel must be inside <Tabs>');
  return ctx;
}

export interface TabProps {
  value: string;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  disabled?: boolean;
  children?: ReactNode;
}

function Tab({ value, leftSection, rightSection, disabled, children }: TabProps): ReactNode {
  const { value: active, setValue, baseId } = useTabs();
  const tabId = `${baseId}-tab-${value}`;
  const panelId = `${baseId}-panel-${value}`;
  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      aria-controls={panelId}
      aria-selected={active === value}
      tabIndex={active === value ? 0 : -1}
      data-active={active === value || undefined}
      disabled={disabled}
      className="rr-tabs__tab"
      onClick={() => setValue(value)}
    >
      {leftSection ? <span className="rr-tabs__tab-section">{leftSection}</span> : null}
      <span>{children}</span>
      {rightSection ? <span className="rr-tabs__tab-section">{rightSection}</span> : null}
    </button>
  );
}

export interface TabsListProps { children?: ReactNode; grow?: boolean; justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' }
function List({ children, grow, justify }: TabsListProps): ReactNode {
  const { orientation } = useTabs();
  // Arrow-key roving.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const tabs = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'));
    const idx = tabs.findIndex((t) => t === document.activeElement);
    if (idx < 0) return;
    const isHor = orientation === 'horizontal';
    const next = (e.key === (isHor ? 'ArrowRight' : 'ArrowDown')) ? (idx + 1) % tabs.length
      : (e.key === (isHor ? 'ArrowLeft' : 'ArrowUp')) ? (idx - 1 + tabs.length) % tabs.length
      : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : -1;
    if (next >= 0) { e.preventDefault(); tabs[next].focus(); tabs[next].click(); }
  };
  return (
    <div
      {...ariaProps.tabs({ orientation })}
      onKeyDown={onKeyDown}
      className={['rr-tabs__list', grow && 'rr-tabs__list--grow'].filter(Boolean).join(' ')}
      style={{ justifyContent: justify }}
    >
      {children}
    </div>
  );
}

export interface TabsPanelProps { value: string; children?: ReactNode; keepMounted?: boolean }
function Panel({ value, children, keepMounted }: TabsPanelProps): ReactNode {
  const { value: active, baseId } = useTabs();
  const panelId = `${baseId}-panel-${value}`;
  const tabId = `${baseId}-tab-${value}`;
  const isActive = active === value;
  if (!isActive && !keepMounted) return null;
  return (
    <div role="tabpanel" id={panelId} aria-labelledby={tabId} hidden={!isActive} className="rr-tabs__panel">
      {children}
    </div>
  );
}

Tab.displayName = 'Tab';
List.displayName = 'TabsList';
Panel.displayName = 'TabsPanel';

interface TabsComponent extends React.FC<TabsProps> {
  List: typeof List;
  Tab: typeof Tab;
  Panel: typeof Panel;
}

function TabsRoot({ value, defaultValue, onChange, orientation = 'horizontal', variant = 'default', color, children, className }: TabsProps): ReactNode {
  const [val, setVal] = useUncontrolled<string>({
    value, defaultValue, finalValue: '', onChange
  });
  const baseId = useId();
  // First Tab's value as the default when none specified.
  let firstValue = '';
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if ((child.type as { displayName?: string }).displayName === 'TabsList') {
      Children.forEach((child.props as TabsListProps).children, (g) => {
        if (!firstValue && isValidElement(g) && (g.type as { displayName?: string }).displayName === 'Tab') {
          firstValue = (g.props as TabProps).value;
        }
      });
    }
  });
  const effective = val || firstValue;

  return (
    <TabsContext.Provider value={{ value: effective, setValue: setVal, orientation, variant, baseId }}>
      <div
        className={['rr-tabs', `rr-tabs--${orientation}`, `rr-tabs--${variant}`, className].filter(Boolean).join(' ')}
        style={color ? { ['--rr-tabs-color' as string]: color } as React.CSSProperties : undefined}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export const Tabs = TabsRoot as TabsComponent;
Tabs.List = List;
Tabs.Tab = Tab;
Tabs.Panel = Panel;
